//defs
import { NEWRELIC_TABLES } from "@checkout-core/constants/NewRelicTables";
import { WebviewState } from "@checkout-core/interfaces/WebviewRefresh";
import { recordNREvent } from "@checkout-core/newrelic";
import { CheckoutActionTypes, CheckoutState } from "@checkout-core/reducers/Checkout";

//internal-deps
import { isEmpty, safeJsonParse } from "@swiggy-private/core-ui";
import { MARKETPLACE_BUSINESS_LINE } from "@swiggy-private/payment-types";

//helpers
import { GlobalHeaderState } from "@checkout-core/utils";
import { SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import { ELIGIBLE_BLS_FOR_WEBVIEW_REFRESH } from "@checkout-core/constants/WebviewRefresh";
import {
    getPersistedOrderDataFromLocalStore,
    cleanupPersistOrderDataInLocalStorage,
} from "./PersistOrderDataLocalStore";
import { CleanupSource } from "@checkout-core/constants/PersistOrderDataInLocalStore";

// added for HTML Caching scenario
export const getBusinessLine = (): string => {
    return GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) || MARKETPLACE_BUSINESS_LINE.REGULAR;
};

export const isBLEligibleForWebviewRefresh = (enabledWebviewRefresh: boolean): boolean => {
    // For SDK clients, switch capability is with themself
    // For redirection flow it is internally managed by ELIGIBLE_BLS_FOR_WEBVIEW_REFRESH
    if (enabledWebviewRefresh) {
        return true;
    }

    const currentBusinessLine = getBusinessLine().toLowerCase() as MARKETPLACE_BUSINESS_LINE;
    return ELIGIBLE_BLS_FOR_WEBVIEW_REFRESH.includes(currentBusinessLine);
};

const getUsersLastState = (shouldRecordNREvent = true): WebviewState | undefined | null => {
    try {
        if (typeof window !== "undefined") {
            const webviewstate = window?.__webview_state__?.getWebViewLastState();
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            const parsedWebviewState: { infoObject?: { order?: { orderId?: string } } } = safeJsonParse(
                webviewstate || "",
            ) || { infoObject: {} };
            const webviewStateOrderId = parsedWebviewState?.infoObject?.order?.orderId;
            if (shouldRecordNREvent) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                    name: "getUsersLastState",
                    isWebviewLastStateEmpty: String(isEmpty(webviewstate)),
                    webviewStateOrderId: webviewStateOrderId || "no-orderId",
                });
            }
            if (!isEmpty(webviewstate)) {
                const parsedRes = safeJsonParse(webviewstate);
                return parsedRes as WebviewState;
            }
            return null;
        }
    } catch (err) {
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_STATE_ERROR,
            errorMessage: JSON.stringify(err),
        });
    }

    return null;
};

export const isWebviewRefreshed = (): boolean => {
    return !isEmpty(getUsersLastState(false));
};

const recordWebviewRefreshState = (enabledWebviewRefresh = false) => {
    try {
        // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
        const parsedWebviewState: { infoObject?: { order?: { orderId?: string } } } = safeJsonParse(
            window?.__webview_state__?.getWebViewLastState() || "",
        ) || { infoObject: {} };
        const webviewStateOrderId = parsedWebviewState?.infoObject?.order?.orderId;
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
            name: "recordWebviewRefreshState",
            enabledWebviewRefresh: String(enabledWebviewRefresh),
            webviewStateOrderId: webviewStateOrderId || "no-orderId",
        });
        if (!isBLEligibleForWebviewRefresh(enabledWebviewRefresh)) {
            return;
        }
        if (typeof window !== "undefined") {
            const webviewstateStr = window?.__webview_state__?.getWebViewLastState();
            if (!isEmpty(webviewstateStr)) {
                const webviewstate = safeJsonParse(webviewstateStr) as WebviewState;
                const parsedWebviewState: { infoObject?: { order?: { orderId?: string } } } = safeJsonParse(
                    window?.__webview_state__?.getWebViewLastState() || "",
                ) || { infoObject: {} };
                const webviewStateOrderId = parsedWebviewState?.infoObject?.order?.orderId;
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_STATE,
                    name: webviewstate?.state || "no-state",
                    paymentCode: webviewstate?.paymentCode || "no-payment-code",
                    enabledWebviewRefresh: `${enabledWebviewRefresh}`,
                    webviewStateOrderId: webviewStateOrderId || "--",
                });
            }
        }
    } catch (err) {
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_STATE_ERROR,
            errorMessage: JSON.stringify(err),
            enabledWebviewRefresh: `${enabledWebviewRefresh}`,
        });
    }
};

const restoreWebviewRefreshState = (enabledWebviewRefresh = false) => {
    if (!isBLEligibleForWebviewRefresh(enabledWebviewRefresh)) {
        return;
    }
    let userLastState = getUsersLastState();
    if (!isEmpty(userLastState)) {
        try {
            const dataFromLocalStorage = getPersistedOrderDataFromLocalStore();
            /* TODO: cleanup once we have webview refresh fix stable */
            try {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                    name: "preDataPickedFromWebLocalStore",
                    enabledWebviewRefresh: String(enabledWebviewRefresh),
                    userLastStateEmpty: String(!isEmpty(dataFromLocalStorage)),
                    userLastStateInternalState: String(dataFromLocalStorage?.state || ""),
                    webviewStateOrderId: dataFromLocalStorage?.infoObject?.order?.orderId || "--",
                });
            } catch {
                // no-op
            }
            if (!isEmpty(dataFromLocalStorage)) {
                try {
                    /* TODO: cleanup once we have webview refresh fix stable */
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                        name: "dataPickedFromWebLocalStore",
                    });
                } catch {
                    // no-op
                }
                // we are treating portal local storage as a valid data source if data is present this will ensure we are not reliant on apps invalid state
                // which doesn't get updated post webview reload/non-refresh in such cases we were getting stale data
                userLastState = dataFromLocalStorage;
            }
            try {
                /* TODO: cleanup once we have webview refresh fix stable */
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                    name: "postDataPickedFromWebLocalStore",
                    enabledWebviewRefresh: String(enabledWebviewRefresh),
                    userLastStateEmpty: String(!isEmpty(dataFromLocalStorage)),
                    userLastStateInternalState: String(dataFromLocalStorage?.state || ""),
                    webviewStateOrderId: dataFromLocalStorage?.infoObject?.order?.orderId || "--",
                });
            } catch {
                // no-op
            }
        } catch (_) {
            userLastState = getUsersLastState();
            try {
                /* TODO: cleanup once we have webview refresh fix stable */
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                    name: "failureDataPickedFromWebLocalStore",
                    enabledWebviewRefresh: String(enabledWebviewRefresh),
                    userLastStateEmpty: String(!isEmpty(userLastState)),
                    userLastStateInternalState: String(userLastState?.state || ""),
                    webviewStateOrderId: userLastState?.infoObject?.order?.orderId || "--",
                });
            } catch {
                // no-op
            }
        }
    }
    const webviewStateOrderId = userLastState?.infoObject?.order?.orderId;
    try {
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_STATE_RESTORE,
            enabledWebviewRefresh: String(enabledWebviewRefresh),
            userLastStateEmpty: String(!isEmpty(userLastState)),
            userLastStateInternalState: String(userLastState?.state || ""),
            webviewStateOrderId: webviewStateOrderId || "--",
        });
    } catch {
        /* No-op */
    }
    if (!isEmpty(userLastState)) {
        if (userLastState.state === CheckoutActionTypes.PROCESS_ORDER) {
            return {
                type: userLastState.state,
                payload: {
                    ...userLastState.infoObject,
                    webviewRefreshExtraParams: {
                        ...userLastState.infoObject?.webviewRefreshExtraParams,
                        amazonPayTransactionId: userLastState.amazonPayTransactionId,
                    },
                },
            };
        }

        const state = {
            type: userLastState.state,
            payload: { ...userLastState.infoObject },
        };

        return state as CheckoutState;
    } else {
        // if app data payload is empty we can empty our local storage as well assuming that webview refresh is not a valid state anymore
        // as apps don't send data in case of new journeys (M2C) we can cleanup our state as well
        cleanupPersistOrderDataInLocalStorage(CleanupSource.APP_WEBVIEW_STATE_EMPTY);
    }
};

export { getUsersLastState, recordWebviewRefreshState, restoreWebviewRefreshState };
